<script lang="ts" setup>
import { ref, watch } from 'vue';
import type { StageVehicle } from '../../lib/api';
import { vTransition } from '@dh-io-owpi/shared/src/directives/v-transition';
import { appName } from '@dh-io-owpi/shared/src/environment';
import type { StageCtasMapped } from '@dh-io-owpi/shared-dynamic-stage/src/composables/useStageCtas';
import type { AemData } from '@dh-io-owpi/shared/src/plugins/aemData';
import { isSmallVersion } from '../../composables/isSmallVersion';
import { usePinnedSalesBar } from './usePinnedSalesBar';
import PriceTag from '../PriceTag/PriceTag.vue';
import { useScrollLock } from '@dh-io-owpi/shared/src/composables/useScrollLock';
import CtaButtons from '../../components/CtaButtons/CtaButtons.vue';
import { useTrackingStore } from '@dh-io-owpi/shared/src/stores/tracking';
import SPASectionLinks from './SPASectionLinks.vue';

defineProps<{
  stageVehicle?: StageVehicle;
  aemData: AemData;
  isDark: boolean;
  ctas: StageCtasMapped;
  purchasePriceTooltip?: string;
  brandId: string | undefined;
}>();

const openedSalesBar = ref(false);
// To ensure the sales bar is closed when the user switches from small to large version
watch(isSmallVersion, (isSmallVersion) => {
  if (!isSmallVersion) {
    openedSalesBar.value = false;
  }
});

useScrollLock(openedSalesBar);
const { elRef, isPinned } = usePinnedSalesBar();
const { track } = useTrackingStore();
const timeoutTransition = 400;
const timeoutTransitionMs = `${timeoutTransition}ms`;

function toggleSalesBar() {
  track({
    timeout: timeoutTransition,
    action: 'salesbar_pi',
    label: openedSalesBar.value ? 'close.button' : 'open.button',
    category: 'feature',
  });
  openedSalesBar.value = !openedSalesBar.value;
}
const isInsideSPA = appName === 'owpi-spa';
</script>
<template>
  <div
    ref="elRef"
    v-transition="isPinned"
    class="owpi-sales-bar"
    :class="{
      'owpi-sales-bar--is-dark': isDark,
      'owpi-sales-bar--is-pinned': isPinned,
    }"
  >
    <div
      class="owpi-sales-bar__fly-out"
      :class="{ 'owpi-sales-bar__fly-out--is-dark': isDark }"
      :aria-expanded="openedSalesBar"
    >
      <div v-if="isInsideSPA" @click="toggleSalesBar">
        <SPASectionLinks :isDark="isDark" />
      </div>
      <CtaButtons class="owpi-sales-bar__fly-out-ctas" :isDark="isDark" :ctas="ctas" trackingAction="salesbar_pi" />
    </div>
    <div class="owpi-sales-bar__wrapper">
      <div class="owpi-sales-bar__content">
        <label class="owpi-sales-bar__model-title wb-type-label">
          {{ $t('dynamicStage.modelTitle') || '...' }}
        </label>
        <h1 class="owpi-sales-bar__model-name wb-type-copy-strong">
          <template v-if="stageVehicle">
            {{ stageVehicle.modelName }}
          </template>
          <wb-skeleton v-else :theme="isDark ? 'dark' : 'light'">&nbsp;</wb-skeleton>
        </h1>
        <label class="owpi-sales-bar__price-title wb-type-label">
          {{ $t('dynamicStage.purchasePricePrefix') || '...' }}
        </label>
        <PriceTag
          class="owpi-sales-bar__price-tag"
          :stageVehicle="stageVehicle"
          :isDark="isDark"
          :purchasePriceTooltip="purchasePriceTooltip"
          :position="isPinned ? 'bottom' : 'top'"
        />
        <button
          class="owpi-sales-bar__chevron wb-round-button wb-round-button--medium"
          :class="{ 'wb-round-button--theme-dark': isDark }"
          :aria-label="!openedSalesBar ? 'Open fly-out' : 'Close fly-out'"
          @click="toggleSalesBar"
        >
          <wb-icon :name="!openedSalesBar ? 'bds/chevron-down/16' : 'bds/close/24'" class="wb-round-button__icon" />
        </button>
        <CtaButtons
          class="owpi-sales-bar__ctas"
          :isDark="isDark"
          :ctas="ctas"
          :trackingAction="isPinned ? 'salesbar_pi' : 'dynamic_stage'"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import '../../variables.sass';

.owpi-sales-bar {
  $root: &;

  --psb-pb: var(--wb-spacing-xs);
  --psb-c-title: var(--wb-grey-45);
  --psb-c-value: var(--wb-grey-20);
  --z-index: 32; // It should be Lower than Header (33) and Hotspot side panel (34)
  --pi-sales-bar-bg: hsla(0, 0%, 100%, 0.72);
  --pi-sales-bar-color: var(--wb-black);
  --pi-sales-bar-shadow: var(--wb-shadow-level-1);
  --pi-sales-bar-border: 1px solid transparent;

  z-index: var(--z-index);
  height: $sales-bar-height;
  margin-bottom: var(--psb-pb);

  &--is-dark {
    --psb-c-title: white;
    --psb-c-value: white;
    --pi-sales-bar-bg: rgba(0, 0, 0, 0.5);
    --pi-sales-bar-color: var(--wb-white);
    --pi-sales-bar-shadow: none;
    --pi-sales-bar-border: 1px solid var(--wb-grey-20);
  }

  &[data-transition] {
    #{$root}__wrapper,
    #{$root}__content {
      transition:
        width 0.2s $wb-move,
        top 0.2s $wb-move,
        padding 0.2s $wb-move;
    }
  }

  &__wrapper {
    height: $sales-bar-height;
    display: flex;
    align-items: center;
    z-index: var(--z-index);

    margin-inline: auto;
    width: var(--wb-grid-width);
    background: var(--pi-sales-bar-bg);
    border: var(--pi-sales-bar-border);
    box-shadow: var(--pi-sales-bar-shadow);
    color: var(--pi-sales-bar-color);
    backdrop-filter: saturate(80%) blur(20px);
  }

  &__content {
    grid-column-gap: var(--wb-spacing-xs);
    grid-row-gap: var(--wb-spacing-3xs);

    display: grid;
    align-items: center;
    grid-template-areas: 'model-title price-title ctas' 'model-name price-tag ctas';
    grid-template-columns: auto 1fr auto;
    grid-template-rows: min-content;

    margin-inline: auto;
    width: 100%;
    padding-inline: var(--wb-spacing-m);

    @include breakpoint-to(mq3) {
      grid-template-areas: 'model-name model-name ctas' 'price-title price-tag ctas';
      grid-column-gap: var(--wb-spacing-xxs);

      #{$root}__model-title {
        display: none;
      }
    }
  }

  &__model-title,
  &__price-title {
    @extend %ellipsis;
    color: var(--psb-c-title);
  }
  &__model-name,
  &__model-title {
    grid-area: model-title;
  }

  &__model-name {
    grid-area: model-name;
    @extend %ellipsis;
    wb-skeleton {
      min-width: 15ch;
      display: block;
    }
  }

  &__price-title {
    grid-area: price-title;
  }

  &__price-tag {
    grid-area: price-tag;
  }

  &__fly-out {
    position: fixed;
    top: -100vh;
    transition: top v-bind(timeoutTransitionMs) $wb-move;
    display: flex;
    flex-direction: column;
    gap: var(--wb-spacing-xs);
    padding: calc($sales-bar-height + var(--wb-spacing-m)) var(--wb-spacing-s) var(--wb-spacing-m);
    width: 100%;
    height: 100vh;
    overflow-y: auto;
    background-color: var(--wb-white);

    &[aria-expanded='true'] {
      top: 0;
    }

    &--is-dark {
      background-color: var(--wb-black);
    }
  }

  & &__chevron {
    display: flex;
    grid-area: ctas;
    transition: background-color 0.2s $wb-move;
  }

  &__ctas {
    display: none;
    grid-area: ctas;
  }

  &__fly-out-ctas {
    display: flex;
    flex-direction: column;
  }

  @include breakpoint-from(mq4) {
    & &__chevron {
      display: none;
    }
    &__ctas {
      display: flex;
      flex-direction: row-reverse;
    }
    &__fly-out {
      display: none;
    }
  }

  &--is-pinned {
    #{$root}__wrapper {
      width: 100%;
    }

    #{$root}__content {
      width: var(--wb-grid-width);
      padding-inline: 0;
    }
  }

  @include breakpoint-to(mq3) {
    --psb-pb: 0;
    height: 0;
    #{$root}__wrapper {
      position: fixed;
      inset: -86px 0 auto 0;
      width: 100%;
    }

    #{$root}__content {
      width: var(--wb-grid-width);
      padding-inline: 0;
    }
  }

  &--is-pinned {
    #{$root}__wrapper {
      position: fixed;
      inset: 0 0 auto 0;
    }
  }

  // avoid displaying the tooltip while workbench is still loading
  wb-tooltip:not(.hydrated) {
    display: none;
  }
}
</style>
